import React from 'react';
import { useMood, MoodCheckInRecord } from '../../contexts/MoodContext';
import { getMood } from './moods';
import { fmtTime } from './moodDates';
import AnimatedMoodIllustration from './visual/AnimatedMoodIllustration';

/**
 * CheckInList — renders check-in records (newest first within a day) with their
 * optional detail, and a per-record delete. Used by the Today, Weekly and
 * Monthly views so a check-in looks and deletes the same everywhere.
 */
export const CheckInList: React.FC<{ records: MoodCheckInRecord[]; showDate?: boolean }> = ({ records, showDate }) => {
  const { deleteCheckIn } = useMood();

  if (records.length === 0) return null;

  return (
    <ul className="mm-ci-list mm-timeline">
      {records.map((r) => {
        const m = getMood(r.mood);
        return (
          <li key={r.id} className="mm-ci" style={{ ['--mood-accent' as string]: m?.accent || '#1a7f7f' }}>
            <span className="mm-ci__marker" aria-hidden="true">
              {m ? <AnimatedMoodIllustration mood={m.id} size="sm" accent={m.accent} /> : null}
            </span>
            <div className="mm-ci__body">
              <div className="mm-ci__head">
                <span className="mm-ci__mood">{m?.label}</span>
                <span className="mm-ci__time">
                  {showDate && `${new Date(r.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} · `}
                  {fmtTime(r.createdAt)}
                </span>
              </div>
              {r.journalEntry && <p className="mm-ci__text">{r.journalEntry}</p>}
              {(r.tags?.length || r.energyLevel || r.notes) && (
                <div className="mm-ci__meta">
                  {r.tags?.map((t) => <span key={t} className="mm-tag">{t}</span>)}
                  {r.energyLevel && <span className="mm-tag mm-tag--energy">⚡ {r.energyLevel}/5</span>}
                  {r.notes && <span className="mm-ci__notes">— {r.notes}</span>}
                </div>
              )}
            </div>
            <button
              type="button"
              className="mm-journal__delete"
              aria-label="Delete check-in"
              title="Delete"
              onClick={() => deleteCheckIn(r.id)}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></svg>
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default CheckInList;
